import { useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import { BsEye } from "react-icons/bs";
import { GiRocket } from "react-icons/gi";
import { IoMdCalendar } from "react-icons/io";
import { MdAlternateEmail, MdMultilineChart } from "react-icons/md";
import Skeleton from "react-loading-skeleton";
import { UserProfileOverviewMemo } from "../../components/UserProfileComponents/Overview";
import useProfilePicture from "../../hooks/useProfilePicture";
import {
  capitalizeFirstLetter,
  parseCarbonDate,
  parseInvestorTypeSimple,
} from "../../utils";
import useAssets from "../../hooks/useAssets";
import { useUser } from "../../hooks/useUser";
import { IoSettingsOutline } from "react-icons/io5";
import { useHistory } from "react-router-dom";

/**
 * User Profile page, that contains all informations about the user.
 * @returns {JSX.Element}
 */
export default function UserProfile() {
  const [userData, setUserData] = useState(null);
  const [assetsData, setAssetsData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [navState, setNavStates] = useState(navbars);
  const { getProfilePictureURI } = useProfilePicture();
  const { getAssetListData } = useAssets();
  const { getUserDetails } = useUser();
  const { push } = useHistory();

  //Get user data and after fetch assets data
  useEffect(() => {
    setIsLoading(true);

    /**
     * Fetch assets data
     * If the user don't follow any asset, set the state as "noData" (String)
     * @param {string} assets All user assets in JSON String format.
     */
    let fetchAssetsData = async (assets) => {
      try {
        let parsedAssets = JSON.parse(assets);
        if (!parsedAssets.length > 0) {
          setAssetsData("noData");
          return null;
        }
      } catch (error) {
        setAssetsData("noData");
        return null;
      }

      let request = await getAssetListData(assets);
      if (request && request.status === 1) {
        setAssetsData(request.data);
      } else {
        setAssetsData("error");
      }
    };

    /**
     * Fetch user data, if cache are usable, then set the userDataCache as state.
     */
    let fetchUserData = async () => {
      //Check if user Data Cache is usable
      let userDataInCache = localStorage.getItem("userDetails");
      if (userDataInCache) {
        let parsedUserDataCache = JSON.parse(userDataInCache);
        setUserData(parsedUserDataCache);
        setIsLoading(false);

        //Call Fetch assets data
        await fetchAssetsData(parsedUserDataCache.preferences.assets);
      } else {
        //Call the userData from external
        let userDetailsData = await getUserDetails();
        if (userDetailsData && userDetailsData.status === 1) {
          setUserData(userDetailsData.data);
          setIsLoading(false);
          if (userDetailsData.data.preferences) {
            //Call Fetch assets data
            await fetchAssetsData(userDetailsData.data.preferences.assets);
          } else {
            setAssetsData("noData");
          }
        } else {
          setUserData("error");
          setIsLoading(false);
        }
      }
    };
    fetchUserData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNavClick = (clickedIndex) => {
    let newNavState = navState.map((item, i) => {
      if (i === clickedIndex) {
        item.status = true;
        return item;
      } else {
        item.status = false;
        return item;
      }
    });
    setNavStates(newNavState);
  };

  if (isLoading) {
    return loadingSkeleton();
  }

  if (userData && userData !== "error") {
    const userBasicInformations = [
      {
        title: "Perfil de investidor",
        data: parseInvestorTypeSimple(userData.investor_type),
        icon: <MdMultilineChart />,
      },
      {
        title: "E-mail",
        data: userData.email,
        icon: <MdAlternateEmail />,
      },
      {
        title: "Membro desde",
        data: parseCarbonDate(userData.created_at),
        icon: <IoMdCalendar />,
      },
    ];

    return (
      <div className="az-content az-content-profile mg-b-20">
        <div className="container mn-ht-100p">
          {
            //User basic informations (Like name, profile picture etc...).
            <div className="az-content-left az-content-left-profile">
              <div className="az-profile-overview">
                <div className="az-img-user mx-auto mg-t-5">
                  <img
                    src={getProfilePictureURI(userData.profile_picture)}
                    alt=""
                  />
                </div>
                <div className="d-flex justify-content-center mg-b-20">
                  <div>
                    <h5 className="az-profile-name">
                      {capitalizeFirstLetter(userData.name)}
                    </h5>
                  </div>
                </div>

                <div className="d-flex justify-content-center">
                  <button
                    class="btn btn-secondary d-inline-flex align-items-center gap-8"
                    style={{
                      borderRadius: 15, height: 40, backgroundColor: "var(--bg-8)", color: "var(--text-1)"
                    }}
                    onClick={() => {
                      push("/account-settings");
                    }}
                  >
                    Editar meu perfil
                    <IoSettingsOutline size={20} />
                  </button>
                </div>

                <hr className="mg-y-30" />
                <label className="az-content-label tx-13 mg-b-20">
                  Informações
                </label>
                <div className="az-profile-social-list">
                  {userBasicInformations.map((item, i) => {
                    return (
                      <div className="media" key={i}>
                        <div className="media-icon" style={{ color: "gray" }}>
                          <i>{item.icon}</i>
                        </div>
                        <div className="media-body">
                          <span className="tx-semibold tx-12">
                            {item.title}
                          </span>
                          <span style={{ wordBreak: "break-word" }}>
                            {item.data}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          }

          <div className="az-content-body az-content-body-profile">
            <nav className="nav az-nav-line">
              {navState.map((item, i) => {
                return (
                  <span
                    key={i}
                    className={
                      item.status
                        ? "nav-link active cursor-pointer"
                        : "nav-link cursor-pointer"
                    }
                    onClick={() => handleNavClick(i)}
                    data-toggle="tab"
                  >
                    <i className="mg-r-5">{item.icon}</i>
                    {item.title}
                  </span>
                );
              })}
            </nav>
            {navState.map((item, i) => {
              if (item.status) {
                return (
                  <div key={i}>
                    {getNavbarContent(item.contentIdentifier, {
                      userData: userData,
                      assetsData: assetsData,
                    })}
                  </div>
                );
              } else {
                return null;
              }
            })}
          </div>
        </div>
      </div >
    );
  } else if (userData === "error") {
    return fatalError();
  } else {
    return <div></div>;
  }
}

const navbars = [
  {
    title: "Visão Geral",
    icon: <BsEye />,
    status: true,
    contentIdentifier: "overview",
  },
];

const getNavbarContent = (contentIdentifier, data) => {
  switch (contentIdentifier) {
    case "overview":
      return <UserProfileOverviewMemo data={data} />;
    default:
      return errorContent();
  }
};

const errorContent = () => {
  return (
    <div className="az-profile-body">
      <div className="text-center">
        <span>
          <GiRocket size={48} />
        </span>
        <h4>Houston, temos um problema!</h4>
        <span>
          Infelizmente, alguns dados não aterrissaram da maneira correta.
          <br />
          <span
            onClick={() => {
              return window.location.reload();
            }}
            className="cursor-pointer tx-primary"
          >
            Clique aqui para tentar novamente.
          </span>
        </span>
      </div>
    </div>
  );
};

const loadingSkeleton = () => {
  return (
    <div className="az-content az-content-profile">
      <div className="container mn-ht-100p">
        <div className="az-content-left az-content-left-profile">
          <div className="az-profile-overview mg-t-20">
            <div className="az-img-user mx-auto">
              <Skeleton height={120} borderRadius={"50%"} />
            </div>
            <div className="d-flex justify-content-center mg-b-20">
              <div>
                <h5 className="az-profile-name">
                  <Skeleton width={120} />
                </h5>
                <p className="az-profile-name-text">
                  <Skeleton width={40} height={10} />
                </p>
              </div>
            </div>
            {!isMobile && <hr className="mg-y-20" />}
          </div>
        </div>

        <div className="az-content-body az-content-body-profile">
          <nav className="nav az-nav-line">
            <a href="?" className="nav-link active" data-toggle="tab">
              <Skeleton width={35} />
            </a>
            <a href="?" className="nav-link" data-toggle="tab">
              <Skeleton width={35} />
            </a>
            <a href="?" className="nav-link" data-toggle="tab">
              <Skeleton width={35} />
            </a>
            <a href="?" className="nav-link" data-toggle="tab">
              <Skeleton width={35} />
            </a>
            <a href="?" className="nav-link" data-toggle="tab">
              <Skeleton width={35} />
            </a>
          </nav>
          <div className="az-profile-body">
            <h4>
              <Skeleton width={200} />
            </h4>

            <div className="row mg-t-20">
              <div className="col-md-4 col-xl-8">
                <div className="az-profile-work-list">
                  <div className="media">
                    <div className="media-logo">
                      <Skeleton width={50} height={50} borderRadius={50} />
                    </div>
                    <div className="media-body">
                      <h6>
                        <Skeleton width={140} />
                      </h6>
                      <Skeleton width={100} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mg-b-20" />
          </div>
        </div>
      </div>
    </div>
  );
};

const fatalError = () => {
  return (
    <div className="az-content pd-t-20 pd-b-20">
      <div className="az-content-body">
        <div className="row row-sm">
          <div className="col-md">
            <div className="card card-minimal-four">
              <div className="card-header">
                <div className="text-center">
                  <span>
                    <GiRocket size={48} />
                  </span>
                  <h4>Houston, temos um problema!</h4>
                  <span>
                    Infelizmente, alguns dados não aterrissaram da maneira
                    correta.
                    <br />
                    <span
                      onClick={() => {
                        return window.location.reload();
                      }}
                      className="cursor-pointer tx-primary"
                    >
                      Clique aqui para tentar novamente.
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
